<template>
  <div
    class="w-full headerSize bg-white dark:bg-dark-400 dark:border-dark-200"
    :class="{
      'border-b': dashboardModalStatePresenter.tabState.value !== 'COURSE'
    }"
  >
    <div class="h-full text-center flex px-4">
      <button
        class="px-8 my-3 rounded cursor-pointer focus:outline-none"
        :class="{
          'bg-gray-200 dark:bg-dark-10 text-arena-lighter': dashboardModalStatePresenter.tabState.value === 'OVERVIEW'
        }"
        @click="click(`OVERVIEW`)"
      >
        <div class="text-xl">
          {{ $t('message.overview') }}
        </div>
      </button>
      <button
        class="px-8 my-3 rounded cursor-pointer focus:outline-none"
        :class="{
          'bg-gray-200 dark:bg-dark-10 text-arena-lighter': dashboardModalStatePresenter.tabState.value === 'DAILY'
        }"
        @click="click(`DAILY`)"
      >
        <div class="text-xl">
          {{ $t('message.daily') }}
        </div>
      </button>
      <button
        class="px-6 my-3 rounded cursor-pointer focus:outline-none"
        :class="{
          'bg-gray-200 dark:bg-dark-10 text-arena-lighter': dashboardModalStatePresenter.tabState.value === 'COURSE'
        }"
        @click="click(`COURSE`)"
      >
        <div class="text-xl">
          {{ $t('message.course') }}
        </div>
      </button>
      <div class="flex-grow h-full" />
      <DropDown
        :itemList="items"
        :selected="changeProgramLanguage"
        :selectedIndex="nested.selectedIndex.value"
        style="margin: 11px 0 12px 8px"
      />
      <IconForm
        class="shadow-md ml-2"
        :height="'37px'"
        :margin_bottom="'10px'"
        :margin_top="'11px'"
        :placeholder="$t('message.enterOtherUser')"
        @enterForm="enterForm"
      />
      <OutlinedIconTextButton
        :border_radius="1"
        class="cursor-pointer"
        :dark_color_list="[
          'text-dark-text',
          'border-dark-200',
          'hover:bg-dark-800',
          'active:text-dark-10',
          'active:bg-dark-800'
        ]"
        :icon="'delete'"
        :padding_left="4"
        :padding_right="6"
        style="margin: 12px 0 11px 8px"
        :text="$t('message.clear')"
        @click="clear()"
      />
      <div class="flex ml-2">
        <img
          v-if="hasAvatarImage"
          alt="user icon"
          class="border rounded-full mb-2"
          :class="isLightMode() ? 'image-style' : 'image-style-dark'"
          :src="initDashboardUser.avatar"
          style="user-drag: none; margin-top: 10px"
        />
        <span
          v-else
          class="material-icons mb-2"
          :class="isLightMode() ? 'image-style' : 'image-style-dark'"
          style="margin-top: 10px"
        >
          account_circle
        </span>
        <div class="ml-2 mr-2 my-4">
          <div class="text-xl text-gray-800 dark:text-dark-text" style="user-select: text">
            {{ initDashboardUser.id }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue'

import { DashboardTabType } from '@/domain/value_objects/dashboard/DashboardTabType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import OutlinedIconTextButton from '@/presentation/components/Button/OutlinedIconTextButton.vue'
import DropDown from '@/presentation/components/DropDown/DropDown.vue'
import IconForm from '@/presentation/components/Input/IconForm.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useDashboardModalStatePresenter } from '@/presentation/composables/dashboard/useDashboardModalStatePresenter'

export default defineComponent({
  components: {
    OutlinedIconTextButton,
    DropDown,
    IconForm
  },
  props: {
    initDashboardUser: {
      type: Object,
      required: true
    }
  },
  setup(props, ctx) {
    const dashboardModalStatePresenter = useDashboardModalStatePresenter()
    const { displayLanguage, isLightMode } = useConfigStatePresenter()

    const targetUserId = ref('')
    const hasAvatarImage = computed(() => props.initDashboardUser.avatar)

    const enterForm = (id: string) => {
      dashboardModalStatePresenter.addDashboardUser(id, displayLanguage.value, isLightMode())
    }

    const changeProgramLanguage = (index: number, value: string) => {
      selectedIndex.value = index
      const trimValue = value.split(' ')[0]
      ctx.emit('changeProgramLanguage', trimValue)
    }

    const clear = () => {
      dashboardModalStatePresenter.clearTargetUsers(displayLanguage.value, isLightMode())
    }

    const click = (tab: DashboardTabType) => {
      dashboardModalStatePresenter.setTabState(tab)
    }

    const selectedIndex = ref(0)
    const items: (ProgramLanguageType | 'All')[] = [
      'All',
      'C',
      'C++',
      'C#',
      'D',
      'JAVA',
      'Scala',
      'Python',
      'Ruby',
      'Rust',
      'JavaScript',
      'Go',
      'Kotlin',
      'PHP',
      'Haskell',
      'OCaml'
    ]

    return {
      dashboardModalStatePresenter,
      enterForm,
      clear,
      click,
      changeProgramLanguage,
      isLightMode,
      hasAvatarImage,
      items,
      nested: { targetUserId, selectedIndex }
    }
  }
})
</script>
<style lang="postcss" scoped>
.headerSize {
  height: 60px;
}
.image-style {
  @apply text-aoj;
  font-size: 40px;
  width: 40px;
  height: 40px;
}
.image-style-dark {
  @apply text-dark-100;
  font-size: 40px;
  width: 40px;
  height: 40px;
}
.primary {
  @apply bg-aoj border-2 border-aoj text-white;

  &:active {
    @apply bg-aoj-click;
  }
  &:focus {
    @apply outline-none;
  }
}

.primary-dark {
  @apply bg-dark-500 border border-dark-100 text-dark-text;

  &:active {
    @apply bg-dark-100 border-dark-10;
  }
  &:focus {
    @apply outline-none;
  }
}
</style>
